import React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { SocialOverviewPayload } from "../../types";
import { getClientBrandMeta } from "./socialBrand";
import { PLATFORM_LABELS, type SocialPlatformFilter } from "./SocialPlatformSelector";

interface Props {
  /** Nombre de la plataforma filtrada; vacío en Visión Global. */
  platformLabel?: string;
  platform?: SocialPlatformFilter;
  clientSlug?: string;
  overview: SocialOverviewPayload | null;
  loading: boolean;
  alertsOpen: boolean;
  onToggleAlerts: () => void;
}

export function SocialKpis({
  overview,
  loading,
  alertsOpen,
  onToggleAlerts,
  platform = "all",
  clientSlug = "",
}: Props) {
  const brandMeta = getClientBrandMeta(clientSlug);

  const isGoogle = platform === "gbp";
  const isMetaIg = platform === "meta_ig";
  const isMetaFb = platform === "meta_fb";
  const isGlobal = platform === "all";

  const platformBadgeLabel = isMetaIg
    ? "Instagram"
    : isMetaFb
    ? "Facebook"
    : isGoogle
    ? "Google BP"
    : "Visión Global · Todas las plataformas";

  const platformColor = isMetaIg
    ? "#e1306c"
    : isMetaFb
    ? "#1877f2"
    : isGoogle
    ? "#34a853"
    : "var(--blue, #7d96ff)";

  const sectionTag = platformBadgeLabel;

  const sectionTitle = isMetaIg
    ? `Interacción y pulso de ${brandMeta.displayName} en Instagram`
    : isMetaFb
    ? `Interacción y pulso de ${brandMeta.displayName} en Facebook`
    : isGoogle
    ? `La nota de ${brandMeta.displayName} en Google, hoy`
    : `La reputación y pulso de ${brandMeta.displayName}, hoy`;

  const windowDays = overview?.window_days ?? 28;

  const sectionSubtitle = isMetaIg
    ? `Cifras de comentarios, negatividad y tiempos de atención en publicaciones y anuncios de Instagram. Comparan los últimos ${windowDays} días con el período anterior.`
    : isMetaFb
    ? `Cifras de comentarios, negatividad y tiempos de atención en publicaciones y anuncios de Facebook. Comparan los últimos ${windowDays} días con el período anterior.`
    : isGoogle
    ? `Cifras de reseñas y valoración para saber si la ficha gana o pierde confianza. Comparan los últimos ${windowDays} días con el período anterior.`
    : `Cifras consolidadas de comunidad, reseñas y atención al cliente en Google, Instagram y Facebook. Comparan los últimos ${windowDays} días con el período anterior.`;

  if (loading && !overview) {
    return (
      <div className="social-editorial-section" style={{ marginBottom: "32px" }}>
        <div className="social-section-tag">{sectionTag}</div>
        <h2 className="social-section-title">{sectionTitle}</h2>
        <div className="social-cards-row-dynamic" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "12px", marginTop: "16px" }}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="social-kpi-card"
              style={{ minHeight: "115px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
            >
              <span style={{ color: "var(--dim, #5e6677)", fontSize: "12px" }}>Cargando métricas…</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!overview) return null;

  const { kpis, universo_actual, universo_previo, window_days } = overview;

  // Delta informativo con estilo editorial
  const getDeltaBadge = (delta: string, isNegativeGood: boolean = false) => {
    if (!delta || delta === "n/d") {
      return (
        <span style={{ fontSize: "11px", color: "var(--dim, #6d7587)" }}>
          {universo_actual < 5 || universo_previo < 5 ? "Muestra inicial" : "Sin variación"}
        </span>
      );
    }

    const isZero = delta === "0.0 pp" || delta === "0%" || delta === "0.00" || delta === "0";
    const isPlus = delta.startsWith("+");
    const isMinus = delta.startsWith("-");
    const isGood = isNegativeGood ? isMinus : isPlus;
    const color = isZero || (!isPlus && !isMinus) ? "var(--dim, #8e96a8)" : isGood ? "var(--green, #2ed573)" : "var(--red, #ff5c5c)";

    return (
      <span style={{ fontSize: "11.5px", fontWeight: 600, color }}>
        {delta} vs. período anterior
      </span>
    );
  };

  interface KpiCardItem {
    key: string;
    label: string;
    value: string;
    subtext: string;
    delta: React.ReactNode;
    accentColor: string;
    isUrgent?: boolean;
    extraNode?: React.ReactNode;
  }

  if (universo_actual === 0) {
    return (
      <div className="social-editorial-section" style={{ marginBottom: "24px" }}>
        <div className="social-section-tag">{sectionTag}</div>
        <h2 className="social-section-title">{sectionTitle}</h2>
        <p className="social-section-subtitle">{sectionSubtitle}</p>
        <div
          style={{
            padding: "24px",
            textAlign: "center",
            background: "var(--surface, #11141b)",
            border: "1px solid var(--line, rgba(255, 255, 255, 0.08))",
            borderRadius: "12px",
            color: "var(--muted, #8e96a8)",
            fontSize: "13px",
          }}
        >
          No hay actividad registrada en {platformBadgeLabel} durante los últimos {window_days} días.
        </div>
      </div>
    );
  }

  const cards: KpiCardItem[] = [];

  // 1. Total interacción / comentarios / reseñas (Card de volumen real)
  cards.push({
    key: "universo",
    label: isGoogle ? "Total reseñas" : isGlobal ? "Total interacciones" : "Total comentarios",
    value: String(universo_actual),
    subtext: `En ${window_days} días evaluados`,
    delta: getDeltaBadge(
      universo_previo > 0
        ? `${universo_actual >= universo_previo ? "+" : ""}${Math.round(((universo_actual - universo_previo) / universo_previo) * 100)}%`
        : "n/d",
      false,
    ),
    accentColor: platformColor,
  });

  // 2. Nota media en Google (SOLO si es Google o Global y TIENE nota real mayor a cero)
  if ((isGoogle || isGlobal) && kpis.nota_media && typeof kpis.nota_media.value === "number" && kpis.nota_media.value > 0) {
    cards.push({
      key: "nota_media",
      label: "Nota media en Google",
      value: kpis.nota_media.value.toFixed(1),
      subtext: "Google BP",
      delta: getDeltaBadge(kpis.nota_media.delta, false),
      accentColor: "#34a853",
    });
  }

  // 3. Negativos (solo si está disponible) con desglose por canal si es Visión Global
  if (kpis.pct_negativos && kpis.pct_negativos.value !== null) {
    const byPlat = kpis.pct_negativos.by_platform;
    const hasPlatformBreakdown = isGlobal && byPlat && Object.keys(byPlat).length > 1;

    cards.push({
      key: "pct_negativos",
      label: isGoogle ? "Reseñas negativas" : "Comentarios negativos",
      value: `${Math.round(kpis.pct_negativos.value)}%`,
      subtext: `${(kpis.pct_negativos.cobertura ?? 100).toFixed(0)}% clasificado`,
      delta: getDeltaBadge(kpis.pct_negativos.delta, true),
      accentColor: "#ff5c5c",
      extraNode: hasPlatformBreakdown ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "5px",
            marginTop: "6px",
            fontSize: "10.5px",
            paddingTop: "6px",
            borderTop: "1px solid var(--line, rgba(255, 255, 255, 0.06))",
          }}
        >
          {byPlat.gbp && (
            <span style={{ color: "#34a853", fontWeight: 650 }}>
              Google: {Math.round(byPlat.gbp.pct)}%
            </span>
          )}
          {byPlat.gbp && (byPlat.meta_ig || byPlat.meta_fb) && (
            <span style={{ color: "var(--dim, #5e6677)" }}>·</span>
          )}
          {byPlat.meta_ig && (
            <span style={{ color: "#e1306c", fontWeight: 550 }}>
              IG: {Math.round(byPlat.meta_ig.pct)}%
            </span>
          )}
          {byPlat.meta_ig && byPlat.meta_fb && (
            <span style={{ color: "var(--dim, #5e6677)" }}>·</span>
          )}
          {byPlat.meta_fb && (
            <span style={{ color: "#1877f2", fontWeight: 550 }}>
              FB: {Math.round(byPlat.meta_fb.pct)}%
            </span>
          )}
        </div>
      ) : null,
    });
  }

  // 4. Tiempo de respuesta (SOLO si hay respuestas registradas con número real, nunca "n/d (sin respuestas...)")
  const dispTime = kpis.tiempo_respuesta_negativos?.display;
  const isInvalidTime =
    !dispTime ||
    dispTime.toLowerCase().includes("sin respuestas") ||
    dispTime.toLowerCase() === "n/d" ||
    dispTime.toLowerCase() === "sin datos";
  if (!isInvalidTime && kpis.tiempo_respuesta_negativos?.value !== null && kpis.tiempo_respuesta_negativos?.value !== undefined) {
    cards.push({
      key: "tiempo_respuesta_negativos",
      label: "Respuesta a negativos",
      value: dispTime || `${Math.round(kpis.tiempo_respuesta_negativos.value)} h`,
      subtext: "Mediana hasta respuesta oficial",
      delta: getDeltaBadge(kpis.tiempo_respuesta_negativos.delta, true),
      accentColor: "#7d96ff",
    });
  }

  // 5. % Respondidos (SOLO si hay respuestas registradas en la fuente)
  const dispResp = kpis.pct_respondidos?.display;
  const isInvalidResp = !dispResp || dispResp.toLowerCase().includes("sin respuestas") || dispResp.toLowerCase() === "n/d";
  if (!isInvalidResp && kpis.pct_respondidos?.value !== null && kpis.pct_respondidos?.value !== undefined) {
    cards.push({
      key: "pct_respondidos",
      label: isGoogle ? "Reseñas respondidas" : "Comentarios respondidos",
      value: dispResp || `${Math.round(kpis.pct_respondidos.value)}%`,
      subtext: "Con respuesta oficial del equipo",
      delta: getDeltaBadge(kpis.pct_respondidos.delta, false),
      accentColor: "#2ed573",
    });
  }

  // 6. Alertas urgentes
  cards.push({
    key: "alertas_urgentes",
    label: isGoogle ? "Reseñas críticas abiertas" : "Comentarios críticos abiertos",
    value: kpis.alertas_urgentes?.value !== null && kpis.alertas_urgentes?.value !== undefined ? String(kpis.alertas_urgentes.value) : "0",
    subtext: Number(kpis.alertas_urgentes?.value ?? 0) > 0 ? "requiere atención urgente" : "al día sin alertas",
    delta: Number(kpis.alertas_urgentes?.value ?? 0) > 0 ? (
      <span style={{ color: "var(--red, #ff5c5c)", fontWeight: 700, fontSize: "11.5px" }}>Requiere atención</span>
    ) : (
      <span style={{ color: "var(--green, #2ed573)", fontWeight: 600, fontSize: "11.5px" }}>Al día</span>
    ),
    accentColor: "#ff5c5c",
    isUrgent: true,
  });

  return (
    <section id="section-social-kpis" className="social-editorial-section" style={{ marginBottom: "36px" }}>
      {/* Encabezado Editorial Dinámico */}
      <div className="social-section-tag">{sectionTag}</div>
      <h2 className="social-section-title">{sectionTitle}</h2>
      <p className="social-section-subtitle">{sectionSubtitle}</p>

      {/* Badges de rol y plataforma */}
      <div className="social-editorial-badges">
        <span
          className="social-role-badge"
          style={{
            background: isMetaIg
              ? "rgba(225, 48, 108, 0.15)"
              : isMetaFb
              ? "rgba(24, 119, 242, 0.15)"
              : isGoogle
              ? "rgba(66, 133, 244, 0.15)"
              : "rgba(125, 150, 255, 0.15)",
            color: platformColor,
            fontWeight: 700,
            borderColor: "transparent",
          }}
        >
          {platformBadgeLabel}
        </span>
        <span className="social-role-badge">Dirección</span>
        <span className="social-role-badge">Comunicaciones</span>
      </div>

      {/* Fila de Tarjetas KPI adaptadas a la plataforma */}
      <div
        className="social-cards-row-dynamic"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${Math.max(1, cards.length)}, minmax(0, 1fr))`,
          gap: "14px",
          marginTop: "14px",
        }}
      >
        {cards.map((c) => {
          const isAlert = c.key === "alertas_urgentes";
          const hasAlerts = isAlert && Number(kpis.alertas_urgentes?.value ?? 0) > 0;

          return (
            <div
              key={c.key}
              className="social-kpi-card"
              style={{
                border: hasAlerts ? "1px solid rgba(255, 92, 92, 0.45)" : undefined,
                borderLeft: hasAlerts ? "3px solid #ff5c5c" : undefined,
                cursor: isAlert ? "pointer" : "default",
              }}
              onClick={isAlert ? onToggleAlerts : undefined}
            >
              <div>
                <span
                  className="social-kpi-card-label"
                  style={{
                    fontSize: "12px",
                    color: "var(--muted, #8e96a8)",
                    fontWeight: 550,
                    display: "block",
                    marginBottom: "8px",
                  }}
                >
                  {c.label}
                </span>
                <div
                  className="social-kpi-card-value"
                  style={{
                    fontSize: "30px",
                    fontWeight: 700,
                    lineHeight: 1.1,
                    letterSpacing: "-0.03em",
                    color: hasAlerts ? "var(--red, #ff5c5c)" : "var(--text, #10131a)",
                    marginBottom: "8px",
                  }}
                >
                  {c.value}
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                {c.delta}
                {isAlert && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", color: "var(--blue, #7d96ff)", fontSize: "11px", fontWeight: 650 }}>
                    {alertsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    {alertsOpen ? "Ocultar" : "Ver"}
                  </span>
                )}
              </div>
              {c.extraNode}
            </div>
          );
        })}
      </div>
    </section>
  );
}

